import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Landmark, PiggyBank } from 'lucide-react';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const SectionRow = ({ s, accounts, value, name, onChange, onName }) => (
  <div className="rounded-lg border p-3 space-y-2" data-testid={`sub-account-section-${s.index}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {s.kind === 'savings' ? <PiggyBank className="h-4 w-4 text-emerald-600 shrink-0" /> : <Landmark className="h-4 w-4 text-slate-500 shrink-0" />}
        <span className="font-medium truncate">{s.title}</span>
        {s.is_primary && <span className="text-[10px] rounded-full bg-slate-100 border px-1.5 py-px text-slate-600">this account</span>}
      </div>
      <span className="text-xs text-muted-foreground shrink-0">{s.rows} rows · {s.first_date} → {s.last_date}</span>
    </div>
    <div className="text-xs text-muted-foreground">Opening {fmt(s.opening)} · Closing {fmt(s.closing)}</div>
    {!s.is_primary && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger data-testid={`sub-account-target-${s.index}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="create">Create linked sub-account (recommended)</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.bank_name} · {a.account_name}</SelectItem>)}
          </SelectContent>
        </Select>
        {value === 'create' && (
          <Input value={name} onChange={(e) => onName(e.target.value)} placeholder={s.suggested_name} data-testid={`sub-account-name-${s.index}`} />
        )}
      </div>
    )}
  </div>
);

export default function GlobalSubAccountDecisionProvider() {
  const { jobs, addJob } = useBackgroundJobs();
  const [job, setJob] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [targets, setTargets] = useState({});
  const [names, setNames] = useState({});
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(() => new Set());

  useEffect(() => {
    const next = (jobs || []).find((j) => j.status === 'awaiting_sub_account_decision' && j.subAccountOffer && !handled.has(j.jobId));
    if (!next || job) return;
    setJob(next);
    axios.get(`${API}/bank-accounts`, { withCredentials: true })
      .then((r) => setAccounts((Array.isArray(r.data) ? r.data : r.data?.accounts || []).filter((a) => a.id !== next.subAccountOffer.parent_bank_account_id)))
      .catch(() => setAccounts([]));
  }, [jobs, job, handled]);

  const finish = () => { if (job) setHandled((h) => new Set(h).add(job.jobId)); setJob(null); setTargets({}); setNames({}); };

  const decide = async (mode) => {
    setBusy(true);
    try {
      const mappings = {};
      (job.subAccountOffer.sections || []).forEach((s) => { if (!s.is_primary) mappings[String(s.index)] = targets[s.index] || 'create'; });
      const { data } = await axios.post(`${API}/bank-feeds/upload-jobs/${job.jobId}/sub-account-decision`,
        { mode, mappings, names, remember: true }, { withCredentials: true });
      if (mode === 'split') {
        const made = (data.created || []).map((c) => c.name).join(', ');
        toast.success(made ? `Created ${made} — each account imports separately` : 'Importing each account separately');
      } else {
        toast.success('Importing as one account');
      }
      addJob({ jobId: job.jobId, filename: job.filename, status: 'queued' });
      finish();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not apply your decision');
    } finally { setBusy(false); }
  };

  if (!job) return null;
  const offer = job.subAccountOffer;
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl [&>button]:hidden" data-testid="sub-account-decision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />This statement contains {offer.sections.length} accounts</DialogTitle>
          <DialogDescription>
            {offer.bank_name} printed separate tables for {offer.sections.map((s) => s.title).join(' and ')}. Keep each one as its own
            account so balances, opening balances and the sweeps between them get the right accounting treatment — transfers
            between them are recognised automatically and never hit income or expenses.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {offer.sections.map((s) => (
            <SectionRow key={s.index} s={s} accounts={accounts} value={targets[s.index] || 'create'} name={names[String(s.index)] || ''}
              onChange={(v) => setTargets((t) => ({ ...t, [s.index]: v }))}
              onName={(v) => setNames((n) => ({ ...n, [String(s.index)]: v }))} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">We'll remember this choice for future {offer.bank_name} statements.</p>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" disabled={busy} onClick={() => decide('merge')} data-testid="sub-account-merge-btn">Import as one account</Button>
          <Button disabled={busy} onClick={() => decide('split')} className="bg-[#108981] hover:bg-[#0b6e68]" data-testid="sub-account-split-btn">
            {busy ? 'Working…' : 'Keep accounts separate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
